import { CP1252_CODE_POINTS, NAMED_HTML_ENTITIES } from "./entities";

export function stripHtml(html: string) {
  return html.replace(/<[^>]*>?/gm, "");
}

/**
 * Decodes common HTML entities to their character equivalents
 */
export function decodeHtmlEntities(html: string): string {
  return html.replace(
    /&(#[xX]?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (match, entity) => {
      if (typeof entity !== "string") {
        return match;
      }
      if (entity.startsWith("#x") || entity.startsWith("#X")) {
        const codePoint = Number.parseInt(entity.slice(2), 16);
        return decodeCodePoint(codePoint, match);
      }
      if (entity.startsWith("#")) {
        const codePoint = Number.parseInt(entity.slice(1), 10);
        return decodeCodePoint(codePoint, match);
      }
      return NAMED_HTML_ENTITIES[entity] ?? match;
    },
  );
}

function decodeCodePoint(codePoint: number, fallback: string): string {
  // NUL, surrogates, and out-of-range references have no readable character
  // (browsers substitute U+FFFD); preserve the raw entity instead.
  if (
    !Number.isInteger(codePoint) ||
    codePoint <= 0 ||
    codePoint > 0x10_ff_ff ||
    (0xd8_00 <= codePoint && codePoint <= 0xdf_ff)
  ) {
    return fallback;
  }
  if (0x80 <= codePoint && codePoint <= 0x9f) {
    return CP1252_CODE_POINTS[codePoint] ?? fallback;
  }
  return String.fromCodePoint(codePoint);
}

/**
 * Convert HTML to readable plain text while PRESERVING block structure: drops
 * comments (incl. Outlook conditionals) and non-readable elements (`<script>`,
 * `<style>`, `<title>`, `<svg>`, …), `<br>`/table rows/`<dt>`/`<dd>` → newline,
 * `<li>` → `"• "`, block elements and `<hr>` → blank line; remaining tags are
 * stripped (quote-aware, so `>` inside attributes is safe, and
 * iteration-bounded so adversarial input can't go quadratic), THEN entities are
 * decoded — decoding last keeps `&lt;b&gt;` as literal text, never re-parsed as
 * markup. Whitespace is normalized per line, blank lines are capped, the stray
 * space before sentence punctuation is dropped, and bullets are kept tight.
 *
 * Use this when the output is shown to a human (e.g. dispute-evidence shop-policy
 * text). For single-line output use {@link extractTextFromHtml}; for a naive
 * tag-strip use {@link stripHtml}.
 *
 * NOTE: a model-tuned variant (`semanticallyStripHtmlTags`) lives in
 * `support-db-util`'s `faq-content-extraction.ts`, and the email body parser keeps
 * its own `bestEffortHtmlToText`; both are intentionally separate for now —
 * converging them onto this canonical helper is a follow-up.
 */
export function htmlToReadableText(html: string): string {
  return (
    decodeHtmlEntities(
      html
        // Elements without readable contents. `|$` matches browser behavior
        // for an unclosed <script>: it swallows the rest of the document.
        .replace(
          /<(script|style|title|noscript|svg|iframe|textarea|template|video|audio)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi,
          " ",
        )
        // Comments can contain `>` and whole tags — remove before tag
        // handling. `-?>` is the spec's abruptly-closed comment (`<!-->`).
        .replace(/<!--(?:-?>|[\s\S]*?(?:-->|$))/g, " ")
        // Doctypes, CDATA, and Word/Outlook `<?…?>` processing instructions:
        // browsers treat these as bogus comments ending at the first `>`.
        .replace(/<[!?][^>]*>?/g, " ")
        .replace(/<li\b(?:[^>"']|"[^"]*"|'[^']*'){0,1000}>/gi, "\n• ")
        // Line breaks: <br> (Shopify's editor emits attribute-carrying
        // `<br data-mce-fragment="1">`), table rows, definition terms/values.
        .replace(
          /<br\b(?:[^>"']|"[^"]*"|'[^']*'){0,1000}>|<\/(tr|dt|dd|summary)>/gi,
          "\n",
        )
        // Block elements (opening OR closing, so unclosed `<p>A<p>B` still
        // separates) → blank line between paragraphs/sections.
        .replace(
          /<\/?(hr|p|div|h[1-6]|ul|ol|blockquote|table|section|article|header|footer|main|aside|nav|dl|pre|figure|figcaption|center|form|fieldset|address|details)\b(?:[^>"']|"[^"]*"|'[^']*'){0,1000}>/gi,
          "\n\n",
        )
        // Table cells → space so adjacent cells don't run together.
        .replace(/<\/?(td|th)\b(?:[^>"']|"[^"]*"|'[^']*'){0,1000}>/gi, " ")
        // Strip remaining tags with NO separator, like a browser: inline tags
        // may split words ("21<sup>st</sup>" is "21st"). Quote-aware.
        .replace(/<\/?[a-zA-Z](?:[^>"']|"[^"]*"|'[^']*'){0,1000}>/g, "")
        // Malformed leftovers (mismatched attribute quote, unterminated tag
        // at EOF): as browsers do, drop to the next `>` or end of input.
        .replace(/<\/?[a-zA-Z][^>]*>?/g, ""),
    )
      // Raw C1 chars are mis-encoded cp1252 text ("You\u0092ll" is "You’ll");
      // map them like the numeric-entity path, and drop other bare controls.
      .replace(
        /[\u0080-\u009f]/g,
        (c) => CP1252_CODE_POINTS[c.charCodeAt(0)] ?? "",
      )
      .replace(/[\u0000-\u0008\u000e-\u001f\u007f]/g, "")
      // Collapse spaces/tabs but keep newlines.
      .replace(/[^\S\n]+/g, " ")
      // Drop the space left before sentence punctuation when an inline tag was
      // stripped (e.g. "<strong>Exchanges</strong>:" → "Exchanges:").
      .replace(/ +([,.;:!?])/g, "$1")
      // Trim spaces around line breaks, then cap blank lines.
      .replace(/ *\n */g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      // Keep consecutive bullets tight (one per line); the blank line before a
      // list (after its lead-in paragraph) is preserved.
      .replace(/(• [^\n]*)\n\n(?=• )/g, "$1\n")
      .trim()
  );
}

export function extractTextFromHtml(html: string): string {
  return html
    .replace(/<[^>]*>?/gm, (match, offset) => {
      const beforeChar = offset > 0 ? html[offset - 1] : "";
      const afterChar =
        offset + match.length < html.length ? html[offset + match.length] : "";
      if (
        beforeChar &&
        afterChar &&
        !/\s/.test(beforeChar) &&
        !/\s/.test(afterChar)
      ) {
        return " ";
      }
      return "";
    })
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Replaces occurrences of &quot; that are not
 * between angle brackets with the character `"`.
 *
 * Quotation marks inside attribute values need to be escaped to
 * avoid ending an attribute early, but quotation marks that aren't
 * inside attribute values don't need to be escaped for any reason.
 */
export function unescapeNonAttributeQuotes(html: string): string {
  let ret = "";
  let insideAngleBrackets = false;
  /** " or ' if we are in a string, null otherwise */
  let delimiterForCurrentString: "'" | '"' | null = null;
  let i = 0;
  const toReplace = "&quot;";
  function setInsideAngleBrackets(newVal: boolean) {
    if (insideAngleBrackets && newVal) {
      throw new Error(
        `HTML is malformed: Found < inside tag at index ${i}. html: ${html}`,
      );
    }
    insideAngleBrackets = newVal;
  }

  while (i < html.length - toReplace.length) {
    if (
      !insideAngleBrackets &&
      html.slice(i, i + toReplace.length) === toReplace
    ) {
      ret += '"';
      i += toReplace.length;
      continue;
    }
    const c = html[i];

    // Quotation marks outside angle brackets don't delimit strings
    if (insideAngleBrackets && (c === '"' || c === "'")) {
      if (delimiterForCurrentString === c) {
        delimiterForCurrentString = null;
      } else {
        delimiterForCurrentString = c;
      }
    }

    // Angle brackets inside strings don't count
    if (delimiterForCurrentString === null) {
      if (c === "<") {
        setInsideAngleBrackets(true);
      } else if (c === ">") {
        setInsideAngleBrackets(false);
      }
    }
    ret += c;
    i++;
  }
  ret += html.substring(i);

  return ret;
}

export function escapeHtml(html: string): string {
  return html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
